// --- Dependencies --------------------------------------------------------------------------------
import { useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyApiResponse, TyBranchDetail, TyCategory } from "@/types";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import { pickLangValue, readLangs } from "@/utils";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import CategoryCard from "@/UI/cards/CategoryCard";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function BranchMenuScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages, defaultLang } = useStoreLanguages();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { data: branchData, loading: branchLoading } = useFetch<
        TyApiResponse<{ branch: TyBranchDetail; overridesPerCategory: Record<string, number> }>
    >(`/branches/${id ?? "x"}`);
    const { data: catData, loading: catLoading, error } = useFetch<TyApiResponse<{ categories: TyCategory[] }>>("/menu/categories");

    const branch = branchData?.data?.branch ?? null;
    const overridesPerCategory = branchData?.data?.overridesPerCategory ?? {};
    const categories = catData?.data?.categories ?? [];
    const loading = branchLoading || catLoading;

    return (
        <div className="branch-menu-screen">
            <ActionsBar
                title={branch?.name ? pickLangValue(readLangs(branch.name, languages), lang ?? defaultLang) : "…"}
                onBack={() => navigate("/branches")}
            />

            <div className="branch-menu-screen-content">
                {loading && (
                    <div className="branch-menu-screen-grid">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="branch-menu-screen-skeleton" />
                        ))}
                    </div>
                )}

                {!loading && error && <p className="branch-menu-screen-message branch-menu-screen-message--error">{error}</p>}

                {!loading && !error && categories.length === 0 && (
                    <div className="branch-menu-screen-empty">
                        <div className="branch-menu-screen-empty-icon">
                            <img src="/icons/category-icon.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="branch-menu-screen-empty-text">
                            <p className="branch-menu-screen-empty-title">{t.menu.emptyCategoriesTitle}</p>
                            <p className="branch-menu-screen-empty-subtitle">{t.menu.emptyCategoriesSubtitle}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && categories.length > 0 && (
                    <div className="branch-menu-screen-grid">
                        {categories.map((category) => (
                            <CategoryCard
                                key={category._id}
                                category={category}
                                overrideCount={overridesPerCategory[category._id] ?? 0}
                                onPress={() =>
                                    navigate(`/branches/${id}/categories/${category._id}`, {
                                        state: { name: category.name, branchName: branch?.name },
                                    })
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
